import axios from 'axios';
import assert from 'assert';
import {
  find, findIndex, get, includes, pick,
} from 'lodash-es';
import { captureProductionException } from '@/helpers/errorLoggers';
import useDelayedCallbackOnPromise from '@/helpers/useDelayedCallbackOnPromise';
import { getFraudnetSessionId } from '@/helpers/fraudnet';
import qs from 'qs';
import { recaptchaSiteKey } from '@/constants';
import { postMessage } from '../postMessage';

// Resolves a Google reCAPTCHA v3 token for the frictionless BIN-attack gate
// (PDT-26303). Resolves null when no site key is configured or the script is
// unavailable, so the charge proceeds unchanged when the feature is off; the
// backend only enforces the token for merchants that have opted in.
function getRecaptchaToken() {
  return new Promise((resolve) => {
    if (typeof window === 'undefined' || !recaptchaSiteKey) {
      resolve(null);
      return;
    }
    const startedAt = Date.now();
    const run = () => {
      const { grecaptcha } = window;
      // Require both execute and ready — a partially-initialised grecaptcha
      // keeps us waiting rather than throwing. Wrap the call so a ready() that
      // throws fails closed (resolves null) instead of leaving the promise
      // unresolved and blocking submission.
      if (grecaptcha && typeof grecaptcha.execute === 'function' && typeof grecaptcha.ready === 'function') {
        try {
          grecaptcha.ready(() => {
            grecaptcha
              .execute(recaptchaSiteKey, { action: 'charge' })
              .then(resolve)
              .catch(() => resolve(null));
          });
        } catch (e) {
          resolve(null);
        }
        return;
      }
      // The script is injected asynchronously in FormSection, so a fast submit
      // can arrive before it loads — wait briefly (up to 3s) rather than
      // sending no token.
      if (Date.now() - startedAt >= 3000) {
        resolve(null);
        return;
      }
      setTimeout(run, 100);
    };
    run();
  });
}

const allowedPaymentStatuses = [
  // These ones are custom
  'INITIAL',
  'FAILED_TO_BEGIN',
  'NEW',
  'BEFORE_CREATED',
  'CREATED',
  'FAILED_TO_CREATE',
  'INTERRUPTED',
  'SYSTEM_SUCCESS',
  'COMPLETED',
  'DECLINED',
  'RECREATE_TO_BEGIN',
  'RECREATE_TO_CONTINUE',
];

function successOrder(dispatch, data) {
  if (data.status === 'SUCCESS') {
    dispatch('setPaymentStatus', ['CREATED']);
    dispatch('setPaymentStatus', ['COMPLETED', data]);
  } else if (data.status === 'FAILED') {
    dispatch('setPaymentStatus', ['DECLINED', data]);
  } else {
    dispatch('setPaymentStatus', ['FAILED_TO_CREATE', 'Payment Declined']);
  }
}

function convertToMetadata(customFieldsObj) {
  if (!customFieldsObj || typeof customFieldsObj !== 'object') return '{}';
  const customFieldsArray = Object.values(customFieldsObj);
  const metadataObj = customFieldsArray.reduce((acc, field) => {
    if (field.value !== null && field.value !== undefined && field.value !== '') {
      acc[field.field_name] = field.value;
    }
    return acc;
  }, {});
  return JSON.stringify(metadataObj);
}


// See ActionResult.vue to understand how it looks
// "type" and "message" are its props

// ActionResult types that may be requested explicitly via setPaymentStatus extraData
// (copy hardcoded in ActionResult, no error-code lookup). Anything outside this list is
// ignored so an unexpected type can't reach ActionResult and throw on types[type] lookup.
const EXPLICIT_RESULT_TYPES = ['threeDsError'];

const actionResultsByStatus = {
  NEW: () => null,
  FAILED_TO_BEGIN(data) {
    if (data) {
      return {
        type: 'customError',
        ...data,
      };
    }
    return { type: 'unknownError' };
  },
  COMPLETED(data) {
    return {
      type: data.is_ach ? 'ach_success' : 'success',
    };
  },
  DECLINED(data) {
    if (data?.type === 'noMoneyError') {
      return { type: 'noMoneyError' };
    }
    return {
      type: 'customError',
      code: data?.code,
    };
  },
  INTERRUPTED: () => ({
    type: 'customError',
    code: 'redirectWindowClosed',
  }),
  FAILED_TO_CREATE(data) {
    if (data) {
      // Explicit ActionResult type whose copy is hardcoded in the component (no code
      // lookup) — used e.g. for the 3DS "couldn't authenticate" failure. Only honor a
      // known type; an unrecognized one falls through to the code/unknownError mapping.
      if (data.type && EXPLICIT_RESULT_TYPES.includes(data.type)) {
        return { type: data.type };
      }
      if (data.code === 'fm000025') {
        return { type: 'unknownError' };
      }
      return {
        type: 'customError',
        code: data.code,
      };
    }
    return { type: 'unknownError' };
  },
  RECREATE_TO_CONTINUE: () => null,
};

const actionProcessingByStatus = {
  NEW: () => null,
  BEFORE_CREATED: () => ({ type: 'paymentLoading' }),
  SYSTEM_SUCCESS: () => ({ type: 'systemSuccess' }),
  FAILED_TO_CREATE: () => null,
  INTERRUPTED: () => null,
  COMPLETED: () => null,
  DECLINED: () => null,
  RECREATE_TO_CONTINUE: () => ({ type: 'simpleLoading' }),
};

export default {
  namespaced: true,

  state: {
    orderParams: {},
    orderData: {
      merchant: {
        name: '',
      },
    },
    customFieldsPayload: [],
    paymentData: {
      cardNumber: '',
      expiryDate: '',
      cvv: '',
      hasRemembered: false,
      country: '',
      city: '',
      firstname: '',
      lastname: '',
      state: '',
      zip: '',
      email: '',
      ewallet: '',
      crypto: '',
      cardDataType: 'saved',
      savedCardId: '',
    },
    paymentACHData: {
      accountNumber: '',
      routingNumber: '',
      accountType: '',
      country: '',
      city: '',
      firstname: '',
      lastname: '',
      businessName: '',
      state: '',
      zip: '',
      email: '',
    },
    currentPlatformId: '',
    actionProcessing: null,
    actionResult: null,
    paymentSubmitted: false,
    paymentStatus: 'INITIAL',
    cards: [],
    isUserCountryConfirmRequested: false,
    isUserCountryRestricted: false,
    isEmailFieldExposed: true,
    isCountryFieldExposed: false,
    isZipFieldExposed: false,
    userIpGeoData: null,
    isZipInvalid: false,
  },

  getters: {
    hasPaymentRequestApi() {
      return Boolean(window.PaymentRequest);
    },
  },

  mutations: {
    cards(state, value) {
      state.cards = value || [];
    },
    orderParams(state, value) {
      state.orderParams = value;
    },
    orderData(state, value) {
      state.orderData = value;
    },
    paymentData(state, value) {
      state.paymentData = value;
    },
    paymentACHData(state, value) {
      state.paymentACHData = value;
    },
    customFieldsPayload(state, value) {
      console.log('value', value);
      state.customFieldsPayload = value;
    },
    currentPlatformId(state, value) {
      state.currentPlatformId = value;
    },
    actionProcessing(state, value) {
      state.actionProcessing = value;
    },
    actionResult(state, value) {
      state.actionResult = value;
    },
    paymentStatus(state, value) {
      assert(includes(allowedPaymentStatuses, value), `Payment status "${value}" is not allowed`);
      state.paymentStatus = value;
    },
    isUserCountryConfirmRequested(state, value) {
      state.isUserCountryConfirmRequested = value;
    },
    isUserCountryRestricted(state, value) {
      state.isUserCountryRestricted = value;
    },
    isEmailFieldExposed(state, value) {
      state.isEmailFieldExposed = value;
    },
    isCountryFieldExposed(state, value) {
      state.isCountryFieldExposed = value;
    },
    isZipFieldExposed(state, value) {
      state.isZipFieldExposed = value;
    },
    userIpGeoData(state, value) {
      state.userIpGeoData = value;
    },
    isZipInvalid(state, value) {
      state.isZipInvalid = value;
    },
  },

  actions: {
    initState({ commit, dispatch }, { orderParams, orderData }) {
      if (orderData.error) {
        dispatch('setPaymentStatus', ['FAILED_TO_BEGIN', orderData.error]);

        return;
      }
      if (orderData.is_already_processed) {
        dispatch('setPaymentStatus', [
          'FAILED_TO_BEGIN',
          {
            type: 'alreadyProcessed',
            receiptUrl: orderData.receipt_url,
          },
        ]);

        return;
      }

      assert(orderData, 'orderData is required to init PaymentFormStore');
      commit('orderData', orderData);

      if (orderParams) {
        commit('orderParams', orderParams);
      }

      if (orderData.platforms) {
        commit('currentPlatformId', orderData.platforms[0].id);
      }
      if (orderData.email) {
        commit('isEmailFieldExposed', false);
        dispatch('setPaymentData', { email: orderData.email });
      }

      const userIpGeoData = orderData.user_ip_data;
      if (userIpGeoData) {
        commit('userIpGeoData', userIpGeoData);
        dispatch('setPaymentData', {
          country: userIpGeoData.country,
          zip: userIpGeoData.zip,
        });
      }

      dispatch('setGeoParams', orderData);
      dispatch('setPaymentStatus', ['NEW']);

      if (orderData.billingAddress) {
        const billing = orderData.billingAddress;

        if (!orderData.email && billing.email) {
          dispatch('setPaymentData', { email: billing.email });
        }

        dispatch('setPaymentData', {
          firstname: billing.first_name,
          lastname: billing.last_name,
          addressline1: billing.address_1,
          addressline2: billing.address_2,
          city: billing.city,
          state: billing.state,
          zip: billing.postcode,
        });
      }
    },

    setPaymentStatus({ commit }, [name, extraData]) {
      commit('paymentStatus', name);
      postMessage(`PAYMENT_${name}`);

      const actionResult = actionResultsByStatus[name];
      if (actionResult) {
        commit('actionResult', actionResult(extraData));
      }

      const actionProcessing = actionProcessingByStatus[name];
      if (actionProcessing) {
        commit('actionProcessing', actionProcessing(extraData));
      }
    },

    setPaymentData({ commit, state }, data) {
      commit('paymentData', {
        ...state.paymentData,
        ...data,
      });
    },
    setPaymentACHData({ commit, state }, data) {
      commit('paymentACHData', {
        ...state.paymentACHData,
        ...data,
      });
    },
    setCustomFieldsPayload({ commit, state }, data) {
      commit('customFieldsPayload', {
        ...state.customFieldsPayload,
        ...data,
      });
    },

    setGeoParams({ commit }, data) {
      // Drop to defaults
      commit('isUserCountryConfirmRequested', false);

      if (get(data, 'user_ip_data.country') === 'US' && !data.user_ip_data.zip) {
        commit('isZipFieldExposed', true);
      }
    },

    async createGooglePayment({ state, rootState, dispatch }, value) {
      dispatch('setPaymentStatus', ['BEFORE_CREATED']);
      const recaptchaToken = await getRecaptchaToken();
      const payload = {
        ...value,
        ...(recaptchaToken ? { recaptcha_token: recaptchaToken } : {}),
        metadata: convertToMetadata(state.customFieldsPayload),
      };
      axios
        .post(`${rootState.apiUrl}/orders/charge`, qs.stringify(payload), {
          headers: {
            Authorization: `Bearer ${state.orderParams.orderToken}`,
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        })
        .then((res) => {
          successOrder(dispatch, res.data);
        })
        .catch((err) => {
          dispatch('setPaymentStatus', ['FAILED_TO_CREATE', 'Payment Declined']);
        });
    },

    createPaypalPayment({ state, rootState, dispatch }, data) {
      dispatch('setPaymentStatus', ['BEFORE_CREATED']);
      try {
        axios
          .post(
            `${rootState.apiUrl}/orders/paypal/${data.order_id}/capture`,
            {},
            {
              headers: {
                Authorization: `Bearer ${data.token}`,
                'Content-Type': 'application/json',
              },
            },
          )
          .then((res) => {
            rootState.chargeId = res.data.data.id || '';
            rootState.isPayPal = true;
            successOrder(dispatch, res.data.data);
          })
          .catch((error) => {
            dispatch('setPaymentStatus', ['DECLINED', { type: 'noMoneyError' }]);
          });
      } catch (error) {
        dispatch('setPaymentStatus', ['DECLINED', { type: 'noMoneyError' }]);
      }
    },
    createErrorPaypalPayment({ state, rootState, dispatch }, data) {
      dispatch('setPaymentStatus', ['DECLINED', { type: 'noMoneyError' }]);
    },

    async createApplePayment({ state, rootState, dispatch }, data) {
      dispatch('setPaymentStatus', ['BEFORE_CREATED']);
      const recaptchaToken = await getRecaptchaToken();
      const payload = {
        ...data.res,
        ...(recaptchaToken ? { recaptcha_token: recaptchaToken } : {}),
        metadata: convertToMetadata(state.customFieldsPayload),
      };
      axios
        .post(`${rootState.apiUrl}/orders/charge`, qs.stringify(payload), {
          headers: {
            Authorization: `Bearer ${state.orderParams.orderToken}`,
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        })
        .then((res) => {
          data.callback(true);
          successOrder(dispatch, res.data);
        })
        .catch((err) => {
          data.callback(false);
          dispatch('setPaymentStatus', ['FAILED_TO_CREATE', 'Payment Declined']);
        });
    },

    async createPayment(
      { state, rootState, dispatch },
      {
        cardNumber,
        expiryDate,
        cvv,
        ewallet,
        crypto,
        email,
        hasRemembered,
        country,
        zip,
        cardDataType,
        savedCardId,
      },
    ) {
      console.log('state', state);
      const { authenticationValue } = state.paymentData;
      const secureCode = authenticationValue;
      dispatch('setPaymentStatus', ['BEFORE_CREATED']);
      const recaptchaToken = await getRecaptchaToken();
      const month = expiryDate.slice(0, 2);
      const year = expiryDate.slice(2, 4);
      const request = {
        order_id: state.orderData.id,
        amount: state.orderData.charge_amount,
        surcharge_applied: state.orderData.merchant.surcharge ?? 0,
        card_number: cardNumber,
        exp_month: month,
        cvv: state.paymentData.cvv ? state.paymentData.cvv : console.log(),
        exp_year: year,
        eci: state.paymentData.eci,
        secure_code: secureCode,
        dsTransId: state.paymentData.dsTransId,
        status: state.paymentData.status,
        currency: 'usd',
        first_name: state.paymentData.firstname,
        last_name: state.paymentData.lastname,
        name: `${state.paymentData.firstname} ${state.paymentData.lastname}`,
        address_line1: state.paymentData.addressline1,
        address_line2: state.paymentData.addressline2,
        state_code: state.paymentData.state,
        city: state.paymentData.city,
        country_code: 'US',
        zip: state.paymentData.zip,
        email: state.paymentData.email,
        recurring: state.orderData.recurring ?? 0,
        // FraudNet session id, for PayPal-processing accounts. An embedded PayPal merchant's
        // card charge is a PayPal order under the hood, so this rides along as the
        // PAYPAL-CLIENT-METADATA-ID header. Empty (and ignored downstream) for every other
        // processor, and whenever FraudNet never loaded.
        client_metadata_id: getFraudnetSessionId(),
        ...(recaptchaToken ? { recaptcha_token: recaptchaToken } : {}),
        metadata: convertToMetadata(state.customFieldsPayload),
      };

      try {
        // Delay is used for the redirect window not to open too soon
        await useDelayedCallbackOnPromise(
          axios.post(`${rootState.apiUrl}/orders/charge`, qs.stringify(request), {
            headers: {
              Authorization: `Bearer ${state.orderParams.orderToken}`,
              'Content-Type': 'application/x-www-form-urlencoded',
            },
          }),
          ({ data }) => {
            rootState.chargeId = data.chargeId || '';
            successOrder(dispatch, data);
          },
          2000,
        );
      } catch (error) {
        dispatch('setPaymentStatus', ['FAILED_TO_CREATE', 'Payment Declined']);
      }
    },

    async createACHPayment(
      { state, rootState, dispatch },
      {
        accountNumber, routingNumber, accountType, firstname, lastname,
        businessName, city, state: stateCode, email,
      },
    ) {
      dispatch('setPaymentStatus', ['BEFORE_CREATED']);

      const recaptchaToken = await getRecaptchaToken();
      const request = {
        order_id: state.orderData.id,
        amount: state.orderData.charge_amount,
        surcharge_applied: state.orderData.merchant.surcharge ?? 0,
        account_number: accountNumber,
        routing_number: routingNumber,
        account_type: accountType,
        first_name: firstname,
        last_name: lastname,
        // Business accounts (CCD) settle under the company name. Gate on the selected account
        // type (not just a non-empty businessName) so a stale value left over from switching
        // back to a personal type isn't sent on a personal (WEB) charge.
        ...(businessName && (accountType || '').startsWith('Business')
          ? { company_name: businessName }
          : {}),
        currency: 'usd',
        // These come from the ACH form (paymentACHData), not the card form's paymentData —
        // an ACH-only checkout never fills the card form, so reading email/city/state from
        // paymentData left them blank (email being blank is what 422s a recurring ACH order).
        state_code: stateCode,
        city,
        country_code: 'US',
        zip: state.paymentData.zip,
        email,
        recurring: state.orderData.recurring ?? 0,
        ...(recaptchaToken ? { recaptcha_token: recaptchaToken } : {}),
        metadata: convertToMetadata(state.customFieldsPayload),
      };

      try {
        // Delay is used for the redirect window not to open too soon
        await useDelayedCallbackOnPromise(
          axios.post(`${rootState.apiUrl}/orders/charge`, qs.stringify(request), {
            headers: {
              Authorization: `Bearer ${state.orderParams.orderToken}`,
              'Content-Type': 'application/x-www-form-urlencoded',
            },
          }),
          ({ data }) => {
            rootState.chargeId = data.chargeId || '';
            rootState.isACH = data.is_ach || '';
            successOrder(dispatch, data);
          },
          2000,
        );
      } catch (error) {
        dispatch('setPaymentStatus', ['FAILED_TO_CREATE', 'Payment Declined']);
      }
    },

    setActionResult({ commit }, value) {
      commit('actionResult', value);
    },
    setActionProcessing({ commit }, value) {
      commit('actionProcessing', value);
    },

    submitUserCountry({ state, commit }) {
      if (state.paymentData.country === 'US') {
        commit('isZipFieldExposed', true);
      } else {
        commit('isZipFieldExposed', false);
      }
      commit('isUserCountryConfirmRequested', false);
    },

    setOrderDataBillingParams({ state, commit }, data) {
      const pickedProps = pick(data, [
        'amount',
        'charge_amount',
        'charge_currency',
        'currency',
        'has_vat',
        'items',
        'total_amount',
        'vat',
        'vat_in_charge_currency',
        'vat_rate',
      ]);

      commit('orderData', {
        ...state.orderData,
        ...pickedProps,
      });
    },
  },
};
